import React from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { ComboNutritionInformation } from 'features/menu/components/product-detail/product-nutrition-information/combo-nutrition-information';
import { ItemNutritionInformation } from 'features/menu/components/product-detail/product-nutrition-information/item-nutrition-information';
import { useProductWizardContext } from 'features/menu/components/product-wizard';

import Modal, { ModalContent, ModalSize } from 'components/modal';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Box } from 'components/ui';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { t } from 'state/intl/translations';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { isItem } from 'state/unified-menu/utils';
import { getDesignToken, primitive } from 'styles/constants';

interface INutritionModalProps {
  onDismiss: () => void;
  /** Optional override for store-less catalog preview (no ProductWizard). */
  selectedProduct?: ILocalizedMenuEntry;
  /** Catalog-preview products representing the current item/picker/combo selection. */
  nutritionItems?: ILocalizedMenuEntry[];
}

/**
 *
 * TODO: I see that we have two nutrition modals.
 * We should either unify them or differentiate their names to clarify their purpose.
 * the other modal is in:  src/components/nutrition/index.tsx
 */
export const NutritionModal: React.FC<INutritionModalProps> = ({
  onDismiss,
  selectedProduct: selectedProductProp,
  nutritionItems,
}) => {
  const { featureDisclaimers } = useFeatureDisclaimers();
  const { calorie } = featureDisclaimers || {};
  const { selectedProduct: wizardSelectedProduct } = useProductWizardContext();
  const selectedProduct = selectedProductProp ?? wizardSelectedProduct;

  if (!selectedProduct) {
    return null;
  }

  // ComboNutritionInformation needs ProductWizard slot state. Catalog preview
  // (no wizard product) may pass selectedProductProp for picker/item/combo and
  // must not fall through to ComboNutritionInformation. Regular combo PDP also
  // forwards selectedProductProp from ProductHome — only treat it as a
  // store-less override when the wizard has no selected product.
  const isCatalogPreviewWithoutWizard = Boolean(selectedProductProp) && !wizardSelectedProduct;
  const showItemNutrition =
    !nutritionItems?.length && (isItem(selectedProduct) || isCatalogPreviewWithoutWizard);

  return (
    <Modal
      size={ModalSize.FULLSCREEN}
      onDismiss={onDismiss}
      amplitudeEventData={{
        modalAppearanceEventMessage: 'Nutritional Information',
      }}
      testID="nutrition-modal"
      backgroundColor={getDesignToken('background-default')}
      header={t('nutritionalInformationAndAllergen')}
      headerTextIsBelowCloseButton
    >
      <ModalContent alignItems="center">
        <ScrollView showsVerticalScrollIndicator={false} focusable>
          {nutritionItems?.length ? (
            <Box>
              {nutritionItems.map(item => (
                <ItemNutritionInformation key={item.id} item={item} />
              ))}
            </Box>
          ) : showItemNutrition ? (
            <ItemNutritionInformation item={selectedProduct} />
          ) : (
            <ComboNutritionInformation />
          )}
          <SanityBlockRenderer
            testID="nutrition-disclaimer"
            content={calorie?.localeRaw}
            textProps={{
              variant: 'copyTwo',
              textAlign: 'left',
              fontSize: 'xs',
              marginBottom: '$4',
            }}
            linkProps={{
              variant: 'copyTwo',
              fontSize: 'xs',
              textDecorationLine: 'underline',
              color: `${primitive.$bbqBrown}`,
            }}
            focusable
          />
        </ScrollView>
      </ModalContent>
    </Modal>
  );
};
